import React, { useState, useRef } from 'react';
import { useParams } from 'react-router-dom';
import { Card } from 'primereact/card';
import { InputText } from 'primereact/inputtext';
import { IconField } from 'primereact/iconfield';
import { InputIcon } from 'primereact/inputicon';
import { Button } from 'primereact/button';
import { Toast } from 'primereact/toast';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { classNames } from 'primereact/utils';
import axiosInstance from '../../../config/axios.config';
import { useReportService, useInvalidateReportService } from '../../../hooks/queries/useReportService';
import GstinOutputComponent from '../output/GstinOutputComponent';
import ConsentCheckbox from './ConsentCheckbox';
import ReportBanner from '../ReportBanner';

const GSTIN_REGEX = /^\d{2}[A-Z]{5}\d{4}[A-Z][1-9A-Z]Z[0-9A-Z]$/;
const GSTIN_LENGTH = 15;

// Kept in step with GstinPdfController::PASSWORD_HINT. Shown next to the
// download button so nobody has to guess at the encrypted file.
const PDF_PASSWORD_HINT = 'Last 6 characters of your GSTIN + "GST" (Example: if GSTIN is 29AAECH2358B1ZS, password is 58B1ZSGST)';

const GstinInputComponent = () => {
    const { serviceId } = useParams();
    const toast = useRef(null);
    const submittingRef = useRef(false);

    const [formData, setFormData] = useState({ id_number: '', consent: false });
    const [errors, setErrors] = useState({});
    const [loading, setLoading] = useState(false);
    const [reportData, setReportData] = useState(null);
    // #161: one shared, cached query instead of a per-component fetch on every
    // mount. Concurrent consumers are deduped into a single request and a
    // revisit is served from cache; the balance is invalidated after a report
    // runs (below), which is what keeps a cached credit figure correct.
    const {
        data: fetchedServiceDetails,
        isFetching: fetchingService,
        isError: serviceError,
    } = useReportService(serviceId);
    const invalidateReportService = useInvalidateReportService();
    const serviceDetails = serviceError
        ? { available_credits: 'N/A', credits_required: 'N/A' }
        : (fetchedServiceDetails || null);

    const handleChange = (e) => {
        const value = (e.target.value || '').toUpperCase().trim();
        setFormData((prev) => ({ ...prev, id_number: value }));
        if (errors.id_number) setErrors((prev) => ({ ...prev, id_number: '' }));
    };

    const handleConsentChange = (e) => {
        setFormData((prev) => ({ ...prev, consent: e.checked }));
        if (errors.consent) setErrors((prev) => ({ ...prev, consent: '' }));
    };

    const validate = () => {
        const next = {};
        if (!formData.id_number) next.id_number = 'GSTIN number is required';
        else if (!GSTIN_REGEX.test(formData.id_number)) next.id_number = 'Invalid GSTIN format (e.g., 08AKWPJ1234H1ZN)';
        if (!formData.consent) next.consent = 'Please confirm consent before submitting';
        return next;
    };

    const submit = async () => {
        setLoading(true);
        try {
            const res = await axiosInstance.post('/surepass/gstin', {
                id_number: formData.id_number,
                api_provider_service_id: parseInt(serviceId, 10),
            });
            if (!res.data.success) throw new Error(res.data.message || 'GSTIN verification failed');
            setReportData(res.data);
            toast.current?.show({ severity: 'success', summary: 'GSTIN verified', detail: 'Report ready below', life: 2000 });
            // Credits just changed. Invalidating updates every mounted consumer at
            // once and is ordered strictly after the deduction.
            invalidateReportService(serviceId);
        } catch (error) {
            console.error('GSTIN verify failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            // Fallbacks only. The server sends a message tailored to what
            // actually went wrong — including the case where this verification
            // isn't enabled on the account — and that must win over a canned
            // line keyed on the status code. Blaming the GSTIN for every 400
            // sent people off re-checking a number that was fine.
            const map = {
                500: 'Internal server error. Please try again later.',
                422: 'Please check your GSTIN and try again.',
                429: 'Too many requests. Please wait a minute before trying again.',
                400: 'Invalid GSTIN. Please verify and retry.',
                404: 'No GSTIN record found.',
            };
            let detail = error.response?.data?.message || map[status] || error.message || 'Verification failed';
            if (status === 402) {
                const c = error.response?.data || {};
                detail = `Insufficient credits. Required: ${c.required_credits ?? 'N/A'}, Available: ${c.current_balance ?? 'N/A'}`;
            }
            // 3.5s is not long enough to read a "contact support" sentence.
            toast.current?.show({ severity: 'error', summary: 'Verification failed', detail, life: detail.length > 60 ? 8000 : 3500 });
        } finally {
            setLoading(false);
            submittingRef.current = false;
        }
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        if (submittingRef.current) return;
        const next = validate();
        if (Object.keys(next).length > 0) {
            setErrors(next);
            toast.current?.show({ severity: 'warn', summary: 'Validation error', detail: 'Please fix the highlighted fields', life: 3000 });
            return;
        }
        setErrors({});
        submittingRef.current = true;
        const required = serviceDetails?.credits_required;
        const available = serviceDetails?.available_credits;
        if (typeof available === 'number' && typeof required === 'number' && available < required) {
            submittingRef.current = false;
            toast.current?.show({ severity: 'error', summary: 'Insufficient credits', detail: `Required: ${required}, Available: ${available}`, life: 3500 });
            return;
        }
        confirmDialog({
            message: (
                <div className="text-sm">
                    <div className="mb-2"><span className="text-600">GSTIN:</span> <span className="text-900 font-medium">{formData.id_number}</span></div>
                    <div className="mb-2"><span className="text-600">Available credits:</span> <span className="text-900 font-medium">{available ?? 'N/A'}</span></div>
                    <div><span className="text-600">Credits to deduct:</span> <span className="text-900 font-medium">{required ?? 'N/A'}</span></div>
                </div>
            ),
            header: 'Confirm GSTIN verification',
            icon: 'pi pi-question-circle text-primary pi-2x',
            acceptLabel: 'Yes, verify',
            rejectLabel: 'Cancel',
            accept: submit,
            reject: () => { submittingRef.current = false; },
            style: { width: '90vw', maxWidth: '450px' },
            draggable: false,
        });
    };

    // Pulls the password-protected PDF as a blob and saves it via an off-DOM
    // anchor. Mirrors EquifaxInputComponent.handleDownloadPDF — under tenancy
    // the static `/storage/...` URL doesn't resolve (tenant-scoped path, no
    // symlink), so the backend streams the bytes instead of handing us a link.
    const handleDownloadPDF = async () => {
        const reportId = reportData?.report_id || reportData?.id || reportData?.data?.report_id;
        if (!reportId) {
            toast.current?.show({ severity: 'warn', summary: 'PDF unavailable', detail: 'Report ID not available', life: 3000 });
            return;
        }

        try {
            const res = await axiosInstance.post(`/surepass/gstin/${reportId}/generate-pdf`, {}, { responseType: 'blob' });

            // Backend sets `attachment; filename="..."`; fall back to a sensible
            // default if the header doesn't survive the trip.
            let filename = `gstin-${reportId}.pdf`;
            const match = /filename="?([^"]+)"?/i.exec(res.headers?.['content-disposition'] || '');
            if (match?.[1]) filename = match[1];

            const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
            const link = document.createElement('a');
            link.href = url;
            link.download = filename;
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);

            toast.current?.show({ severity: 'success', summary: 'Download started', detail: filename, life: 2500 });
        } catch (error) {
            // Error bodies are JSON but arrive as a Blob because of responseType,
            // so read it back before falling through to the status mapping.
            let serverMessage = null;
            if (error.response?.data instanceof Blob) {
                try {
                    serverMessage = JSON.parse(await error.response.data.text())?.message;
                } catch (_) { /* ignore — fall through to status mapping */ }
            }

            console.error('GSTIN PDF download failed:', error?.message || 'Unknown error');
            const status = error.response?.status;
            const detail = serverMessage || (
                status === 404 ? 'Report not found. Please regenerate the report first.' :
                status === 400 ? 'No data available for PDF generation.' :
                status === 500 ? 'Internal server error. Please try again later.' :
                'PDF download failed'
            );
            toast.current?.show({ severity: 'error', summary: 'Download failed', detail, life: 3500 });
        }
    };

    const handleAnother = () => {
        setReportData(null);
        setFormData({ id_number: '', consent: false });
        setErrors({});
    };

    return (
        <>
            <Toast ref={toast} />
            <ConfirmDialog />
            <div className="grid">
                <div className="col-12">
                    <ReportBanner
                        icon="pi-verified"
                        title="GSTIN Verification"
                        subtitle="Verify GST registration details for a GSTIN number"
                        availableCredits={serviceDetails?.available_credits}
                        requiredCredits={serviceDetails?.credits_required}
                        loading={fetchingService}
                    />
                    {!reportData ? (
                        <Card>
                            <form onSubmit={handleSubmit} noValidate>
                                <div className="grid">
                                    <div className="field col-12 md:col-6 lg:col-5">
                                        <label htmlFor="gstin" className="block font-medium text-sm mb-2">
                                            GSTIN Number <span className="text-red-500">*</span>
                                        </label>
                                        <IconField iconPosition="left" className="w-full">
                                            <InputIcon className="pi pi-verified" />
                                            <InputText
                                                id="gstin"
                                                value={formData.id_number}
                                                onChange={handleChange}
                                                placeholder="e.g. 08AKWPJ1234H1ZN"
                                                maxLength={GSTIN_LENGTH}
                                                autoCapitalize="characters"
                                                spellCheck={false}
                                                disabled={loading}
                                                className={classNames('w-full p-inputtext-sm font-mono', { 'p-invalid': !!errors.id_number })}
                                                aria-describedby="gstin-help"
                                                aria-invalid={!!errors.id_number}
                                            />
                                        </IconField>
                                        {errors.id_number && (
                                            <small id="gstin-help" className="p-error block mt-1" role="alert">
                                                <i className="pi pi-exclamation-circle mr-1" aria-hidden="true" />
                                                {errors.id_number}
                                            </small>
                                        )}
                                    </div>
                                </div>
                                <ConsentCheckbox
                                    checked={formData.consent}
                                    onChange={handleConsentChange}
                                    error={errors.consent}
                                    reportType="gstin-verification"
                                />
                                <div className="flex justify-content-end mt-3">
                                    <Button
                                        type="submit"
                                        label="Verify GSTIN"
                                        icon="pi pi-arrow-right"
                                        iconPos="right"
                                        loading={loading}
                                        disabled={loading}
                                        size="small"
                                    />
                                </div>
                            </form>
                        </Card>
                    ) : (
                        <>
                            <div className="flex justify-content-end mb-3">
                                <Button
                                    label="Verify another"
                                    icon="pi pi-refresh"
                                    severity="secondary"
                                    outlined
                                    size="small"
                                    onClick={handleAnother}
                                />
                            </div>
                            <GstinOutputComponent
                                reportData={reportData}
                                onDownloadPDF={handleDownloadPDF}
                                pdfPasswordHint={PDF_PASSWORD_HINT}
                            />
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default GstinInputComponent;
